import { BasePage } from '../BasePage'
import { MainTitle } from '@/components/title/MainTitle'
import { useFetch } from '@/hooks/useFetch'
import { showError, showSuccess } from '@/shared/toast'
import { DestinationFormData } from '@/types/destinationFormData'
import { DestinationForm } from '@/features/DestinationForm'
import { splitPostalCode, trimFull } from '@/utils/inputFormatter'
import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { initialDestinationFormData } from '@/constants/initialDestinationFormData'
import { SingleValue } from 'react-select'
import { useSaveDestination } from '@/hooks/features/destination/useSaveDestination'
import { useDestinationValidation } from '@/hooks/features/destination/useDestinationValidation'
import { isFutureDate } from '@/utils/dateUtils'
import { isAxiosError } from 'axios'
import { isDisabledAtom } from '@/store/state'
import { useSetAtom } from 'jotai'
import { CancelFormConfirmModal } from '@/features/CancelFormConfirmModal/Component'

const style = {
  marginBottom: '1rem',
}

export const DestinationFormPage = () => {
  const { save } = useSaveDestination()
  const { validate } = useDestinationValidation()
  const id = useParams().id
  const title = id ? '送達先更新' : '送達先登録'
  const navigate = useNavigate()
  const startDateRef = useRef<string>('')
  const setIsDisabled = useSetAtom(isDisabledAtom)

  const [formData, setFormData] = useState<DestinationFormData>(
    initialDestinationFormData,
  )
  const [initialFormData, setInitialFormData] = useState<DestinationFormData>(
    initialDestinationFormData,
  )
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const normalizeFormData = (data: DestinationFormData): DestinationFormData => {
    return Object.fromEntries(
      Object.entries(initialDestinationFormData).map(([key, initialValue]) => [
        key,
        data[key as keyof DestinationFormData] ?? initialValue,
      ]),
    ) as DestinationFormData
  }
  const trimFormData = (formData: DestinationFormData): DestinationFormData => {
    return {
      ...formData,
      name: trimFull(formData.name),
      addressLine1: trimFull(formData.addressLine1),
      addressLine2: trimFull(formData.addressLine2),
    }
  }
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()
    setIsDisabled(true)

    const trimmedFormData = trimFormData(formData)

    const errors = validate(trimmedFormData)
    if (Object.keys(errors).length > 0) {
      setErrors(errors)
      setIsDisabled(false)
      return
    }
    save(trimmedFormData, startDateRef.current, id)
      .then((res: any) => {
        showSuccess(`送達先情報を${id ? '更新' : '登録'}しました。`)
        if (res.data.id) {
          navigate(`/destinations/${res.data.id}/view`)
        } else {
          navigate('/destinations')
        }
      })
      .catch((err) => {
        if (isAxiosError(err) && err.response?.status === 422) {
          const responseErrors = err.response.data.errors
          // TODO Laravel側のバリデーションと合わせて見直し
          if (responseErrors.destinationCode) {
            setErrors((prev) => ({
              ...prev,
              destinationCode: responseErrors.destinationCode.join('\n'),
            }))
            return
          }
        }
        showError(`送達先情報の${id ? '更新' : '登録'}に失敗しました。`)
      })
      .finally(() => {
        setIsDisabled(false)
      })
  }
  function handleChange(e: ChangeEvent<HTMLInputElement>): void {
    const { name, value } = e.target

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
    setErrors((prev) => ({ ...prev, [name]: '' }))
    if ((name === 'postalCode1' || name === 'postalCode2') && value) {
      setErrors((prev) => ({ ...prev, postalCode: '' }))
    }
  }
  const handleSelectChange = (
    newValue: SingleValue<{
      label: string
      value: string
    }>,
  ) => {
    setFormData((prev) => ({
      ...prev,
      prefectureCode: newValue?.value || '',
    }))
    setErrors((prev) => ({ ...prev, prefectureCode: '' }))
  }
  const {
    data,
    error,
  }: { data: DestinationFormData | null; error: Error | null } = useFetch(
    id ? `/api/destinations/${id}` : null,
  )

  useEffect(() => {
    setIsDisabled(false)

    if (id && data) {
      const normalizedData = normalizeFormData(data)
      const { postalCode1, postalCode2 } = splitPostalCode(normalizedData.postalCode)
      const startDateYmd = normalizedData.startDate.slice(0, 10)
      const nextFormData = {
        ...normalizedData,
        postalCode1,
        postalCode2,
        startDate: startDateYmd,
      }
      if (!isFutureDate(normalizedData.startDate)) {
        nextFormData.startDate = ''
      }
      setFormData(nextFormData)
      setInitialFormData(nextFormData)
      startDateRef.current = startDateYmd
    }
  }, [id, data])

  useEffect(() => {
    if (error) {
      showError('送達先情報の取得に失敗しました。')
    }
  }, [error])

  const handleCancel = () => {
    if (JSON.stringify(formData) !== JSON.stringify(initialFormData)) {
      setShowCancelConfirmModal(true)
      return
    }
    navigate('/destinations')
  }

  return (
    <>
      <BasePage title={title}>
        <MainTitle addStyle={style}>{title}</MainTitle>
        <DestinationForm
          onSubmit={handleSubmit}
          onChange={handleChange}
          onSelectChange={handleSelectChange}
          onCancel={handleCancel}
          formData={formData}
          errors={errors}
          id={id}
        />
      </BasePage>
      {showCancelConfirmModal && (
        <CancelFormConfirmModal
          onConfirm={() => navigate('/destinations')}
          onCancel={() => setShowCancelConfirmModal(false)}
        />
      )}
    </>
  )
}
